import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Notifications,
  AccountCircle,
  Logout,
  PersonAdd,
  LocalHospital,
  CalendarToday,
  Warning,
  VpnKey
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useNavigate } from 'react-router-dom';
import { fixS3Url } from '../../utils/s3Utils';

const DRAWER_WIDTH = 240;

export default function Header() {
  const { user, logout } = useAuth();
  const { recentActivities } = useNotifications();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleResetPassword = () => {
    navigate('/change-password');
    handleMenuClose();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'patient':
        return <PersonAdd color="primary" />;
      case 'doctor':
        return <LocalHospital color="success" />;
      case 'appointment':
        return <CalendarToday color="warning" />;
      case 'critical':
        return <Warning color="error" />;
      default:
        return <Notifications color="info" />;
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: `calc(100% - ${DRAWER_WIDTH}px)`,
        ml: `${DRAWER_WIDTH}px`,
        backgroundColor: 'background.paper',
        color: 'text.primary',
        boxShadow: 1,
      }}
    >
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary' }}>
          {user?.role === 'superadmin' ? 'Super Admin Dashboard' : user?.role === 'doctor' ? 'Doctor Dashboard' : 'Marketing Dashboard'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {user?.role === 'superadmin' && (
            <Tooltip title="Notifications">
              <IconButton color="inherit" onClick={handleNotificationOpen}>
                <Badge badgeContent={recentActivities.length} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Account">
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar
                src={fixS3Url(user?.profile_image) ? `${fixS3Url(user?.profile_image)}?t=${Date.now()}` : undefined}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: user?.profile_image ? 'transparent' : 'primary.main',
                  border: user?.profile_image ? '2px solid #e0e0e0' : 'none'
                }}
              >
                {!user?.profile_image && ((user?.full_name || user?.username)?.charAt(0) || 'U')}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Notification Menu */}
        <Menu
          anchorEl={notificationAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationClose}
          PaperProps={{
            sx: {
              width: { xs: 'calc(100vw - 32px)', sm: '90vw', md: 400 },
              maxWidth: 500
            }
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
              Recent Activities
            </Typography>
          </Box>
          <Divider />
          <Box
            sx={{
              maxHeight: 320, // Approximately 5 items (64px each)
              overflowY: 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.3)',
                },
              },
            }}
          >
            {recentActivities.length > 0 ? (
              recentActivities.map((activity) => (
                <MenuItem
                  key={activity.id}
                  onClick={() => {
                    // Navigate to the appropriate page based on activity type with highlight
                    if (activity.navigateTo) {
                      console.log(`🔗 Header Notification: Navigating to ${activity.navigateTo} for ${activity.type} activity with entityId: ${activity.entityId}`);
                      navigate(activity.navigateTo, {
                        state: { highlightId: activity.entityId }
                      });
                    }
                    handleNotificationClose();
                  }}
                  sx={{
                    py: { xs: 1.5, md: 1 },
                    px: { xs: 1.5, md: 2 },
                    cursor: activity.navigateTo ? 'pointer' : 'default',
                    '&:hover': {
                      backgroundColor: activity.navigateTo ? 'action.hover' : 'transparent'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: { xs: 36, md: 40 } }}>
                    {getActivityIcon(activity.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.text}
                    secondary={activity.time}
                    primaryTypographyProps={{
                      fontSize: { xs: '0.813rem', sm: '0.875rem' },
                      sx: { wordBreak: 'break-word' }
                    }}
                    secondaryTypographyProps={{
                      fontSize: { xs: '0.688rem', sm: '0.75rem' }
                    }}
                  />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <ListItemText
                  primary="No recent activities"
                  primaryTypographyProps={{
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }}
                />
              </MenuItem>
            )}
          </Box>
        </Menu>

        {/* Account Menu */}
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleProfile}>
            <AccountCircle sx={{ mr: 1 }} />
            Profile
          </MenuItem>
          <MenuItem onClick={handleResetPassword}>
            <VpnKey sx={{ mr: 1 }} />
            Change Password
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <Logout sx={{ mr: 1 }} />
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
